import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance, AxiosResponse } from 'axios';
import { type ApiError, addActionNameToMutationResult, useAxiosInstance } from 'shared/hooks/utils';

const URL = '/api_credential';

// Rotating the secret mints a fresh value and returns it exactly once, at
// generation time; no read ever exposes it again. The caller must capture it
// from this response.
export type RegenerateApiSecretResponse = AxiosResponse<{ secret_id: string }>;

export async function regenerateApiSecretFn(
  axiosInstance: Promise<AxiosInstance>,
): Promise<RegenerateApiSecretResponse> {
  const instance = await axiosInstance;

  return instance.post(URL);
}

export function useRegenerateApiSecret() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<RegenerateApiSecretResponse, ApiError, void>({
    mutationFn: () => regenerateApiSecretFn(axiosInstance),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['apiCredentials'] });
      client.invalidateQueries({ queryKey: ['companyConfigurationV2'] });
    },
  });

  return addActionNameToMutationResult<
    RegenerateApiSecretResponse,
    ApiError,
    void,
    'regenerateApiSecret'
  >('regenerateApiSecret', mutation);
}
